import { isNil } from 'es-toolkit';
/**
 * ISO 4217 currency data with decimal places.
 * decimalPlaces indicates the number of decimal digits used for amounts in that currency
 * (e.g. 2 for EUR/USD/CZK, 0 for JPY, 3 for KWD).
 */

// `ICurrency` is defined by `schemas/currency.schema` and re-exported from the package root as a
// type; this module owns the runtime table and its lookup helpers.
import type { ICurrency } from '../schemas/currency.schema';

export const CURRENCIES = [
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', decimalPlaces: 2 },
  { code: 'AFN', name: 'Afghan Afghani', symbol: '؋', decimalPlaces: 2 },
  { code: 'ALL', name: 'Albanian Lek', symbol: 'L', decimalPlaces: 2 },
  { code: 'AMD', name: 'Armenian Dram', symbol: '֏', decimalPlaces: 2 },
  { code: 'ANG', name: 'Netherlands Antillean Guilder', symbol: 'ƒ', decimalPlaces: 2 },
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', decimalPlaces: 2 },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$', decimalPlaces: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'AWG', name: 'Aruban Florin', symbol: 'ƒ', decimalPlaces: 2 },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', decimalPlaces: 2 },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', decimalPlaces: 2 },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', decimalPlaces: 2 },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', decimalPlaces: 2 },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', decimalPlaces: 3 },
  { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu', decimalPlaces: 0 },
  { code: 'BMD', name: 'Bermudian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'BND', name: 'Brunei Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs.', decimalPlaces: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimalPlaces: 2 },
  { code: 'BSD', name: 'Bahamian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'BTN', name: 'Bhutanese Ngultrum', symbol: 'Nu.', decimalPlaces: 2 },
  { code: 'BWP', name: 'Botswanan Pula', symbol: 'P', decimalPlaces: 2 },
  { code: 'BYN', name: 'Belarusian Ruble', symbol: 'Br', decimalPlaces: 2 },
  { code: 'BZD', name: 'Belize Dollar', symbol: 'BZ$', decimalPlaces: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'CDF', name: 'Congolese Franc', symbol: 'FC', decimalPlaces: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimalPlaces: 2 },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$', decimalPlaces: 0 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimalPlaces: 2 },
  { code: 'COP', name: 'Colombian Peso', symbol: '$', decimalPlaces: 2 },
  { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡', decimalPlaces: 2 },
  { code: 'CUP', name: 'Cuban Peso', symbol: '₱', decimalPlaces: 2 },
  { code: 'CVE', name: 'Cape Verdean Escudo', symbol: '$', decimalPlaces: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', decimalPlaces: 2 },
  { code: 'DJF', name: 'Djiboutian Franc', symbol: 'Fdj', decimalPlaces: 0 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimalPlaces: 2 },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$', decimalPlaces: 2 },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'د.ج', decimalPlaces: 2 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'Nfk', decimalPlaces: 2 },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br', decimalPlaces: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', decimalPlaces: 2 },
  { code: 'FJD', name: 'Fijian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'FKP', name: 'Falkland Islands Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾', decimalPlaces: 2 },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', decimalPlaces: 2 },
  { code: 'GIP', name: 'Gibraltar Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'GMD', name: 'Gambian Dalasi', symbol: 'D', decimalPlaces: 2 },
  { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', decimalPlaces: 0 },
  { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q', decimalPlaces: 2 },
  { code: 'GYD', name: 'Guyanaese Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'HNL', name: 'Honduran Lempira', symbol: 'L', decimalPlaces: 2 },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', decimalPlaces: 2 },
  { code: 'HTG', name: 'Haitian Gourde', symbol: 'G', decimalPlaces: 2 },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', decimalPlaces: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimalPlaces: 2 },
  { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪', decimalPlaces: 2 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimalPlaces: 2 },
  { code: 'IQD', name: 'Iraqi Dinar', symbol: 'ع.د', decimalPlaces: 3 },
  { code: 'IRR', name: 'Iranian Rial', symbol: '﷼', decimalPlaces: 2 },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr', decimalPlaces: 0 },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$', decimalPlaces: 2 },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', decimalPlaces: 3 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimalPlaces: 0 },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', decimalPlaces: 2 },
  { code: 'KGS', name: 'Kyrgystani Som', symbol: 'лв', decimalPlaces: 2 },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛', decimalPlaces: 2 },
  { code: 'KMF', name: 'Comorian Franc', symbol: 'CF', decimalPlaces: 0 },
  { code: 'KPW', name: 'North Korean Won', symbol: '₩', decimalPlaces: 2 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimalPlaces: 0 },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', decimalPlaces: 3 },
  { code: 'KYD', name: 'Cayman Islands Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', decimalPlaces: 2 },
  { code: 'LAK', name: 'Laotian Kip', symbol: '₭', decimalPlaces: 2 },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'ل.ل', decimalPlaces: 2 },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: '₨', decimalPlaces: 2 },
  { code: 'LRD', name: 'Liberian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'LSL', name: 'Lesotho Loti', symbol: 'L', decimalPlaces: 2 },
  { code: 'LYD', name: 'Libyan Dinar', symbol: 'ل.د', decimalPlaces: 3 },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', decimalPlaces: 2 },
  { code: 'MDL', name: 'Moldovan Leu', symbol: 'L', decimalPlaces: 2 },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar', decimalPlaces: 2 },
  { code: 'MKD', name: 'Macedonian Denar', symbol: 'ден', decimalPlaces: 2 },
  { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K', decimalPlaces: 2 },
  { code: 'MNT', name: 'Mongolian Tugrik', symbol: '₮', decimalPlaces: 2 },
  { code: 'MOP', name: 'Macanese Pataca', symbol: 'MOP$', decimalPlaces: 2 },
  { code: 'MRU', name: 'Mauritanian Ouguiya', symbol: 'UM', decimalPlaces: 2 },
  { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨', decimalPlaces: 2 },
  { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'Rf', decimalPlaces: 2 },
  { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK', decimalPlaces: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', decimalPlaces: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimalPlaces: 2 },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT', decimalPlaces: 2 },
  { code: 'NAD', name: 'Namibian Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', decimalPlaces: 2 },
  { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$', decimalPlaces: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimalPlaces: 2 },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: '₨', decimalPlaces: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', decimalPlaces: 3 },
  { code: 'PAB', name: 'Panamanian Balboa', symbol: 'B/.', decimalPlaces: 2 },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/.', decimalPlaces: 2 },
  { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'K', decimalPlaces: 2 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', decimalPlaces: 2 },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', decimalPlaces: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimalPlaces: 2 },
  { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲', decimalPlaces: 0 },
  { code: 'QAR', name: 'Qatari Rial', symbol: 'ر.ق', decimalPlaces: 2 },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', decimalPlaces: 2 },
  { code: 'RSD', name: 'Serbian Dinar', symbol: 'дин.', decimalPlaces: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', decimalPlaces: 2 },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw', decimalPlaces: 0 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', decimalPlaces: 2 },
  { code: 'SBD', name: 'Solomon Islands Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'SCR', name: 'Seychellois Rupee', symbol: '₨', decimalPlaces: 2 },
  { code: 'SDG', name: 'Sudanese Pound', symbol: 'ج.س.', decimalPlaces: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimalPlaces: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'SHP', name: 'Saint Helena Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'SLE', name: 'Sierra Leonean Leone', symbol: 'Le', decimalPlaces: 2 },
  { code: 'SOS', name: 'Somali Shilling', symbol: 'S', decimalPlaces: 2 },
  { code: 'SRD', name: 'Surinamese Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'SSP', name: 'South Sudanese Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'STN', name: 'São Tomé and Príncipe Dobra', symbol: 'Db', decimalPlaces: 2 },
  { code: 'SYP', name: 'Syrian Pound', symbol: '£', decimalPlaces: 2 },
  { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'L', decimalPlaces: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', decimalPlaces: 2 },
  { code: 'TJS', name: 'Tajikistani Somoni', symbol: 'SM', decimalPlaces: 2 },
  { code: 'TMT', name: 'Turkmenistani Manat', symbol: 'T', decimalPlaces: 2 },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت', decimalPlaces: 3 },
  { code: 'TOP', name: 'Tongan Paʻanga', symbol: 'T$', decimalPlaces: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimalPlaces: 2 },
  { code: 'TTD', name: 'Trinidad and Tobago Dollar', symbol: 'TT$', decimalPlaces: 2 },
  { code: 'TWD', name: 'New Taiwan Dollar', symbol: 'NT$', decimalPlaces: 2 },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', decimalPlaces: 2 },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', decimalPlaces: 2 },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh', decimalPlaces: 0 },
  { code: 'USD', name: 'US Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U', decimalPlaces: 2 },
  { code: 'UZS', name: 'Uzbekistan Som', symbol: 'лв', decimalPlaces: 2 },
  { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'Bs', decimalPlaces: 2 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', decimalPlaces: 0 },
  { code: 'VUV', name: 'Vanuatu Vatu', symbol: 'VT', decimalPlaces: 0 },
  { code: 'WST', name: 'Samoan Tala', symbol: 'WS$', decimalPlaces: 2 },
  { code: 'XAF', name: 'CFA Franc BEAC', symbol: 'FCFA', decimalPlaces: 0 },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: '$', decimalPlaces: 2 },
  { code: 'XOF', name: 'CFA Franc BCEAO', symbol: 'CFA', decimalPlaces: 0 },
  { code: 'XPF', name: 'CFP Franc', symbol: '₣', decimalPlaces: 0 },
  { code: 'YER', name: 'Yemeni Rial', symbol: '﷼', decimalPlaces: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimalPlaces: 2 },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', decimalPlaces: 2 },
  { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: '$', decimalPlaces: 2 }
] as const satisfies readonly ICurrency[];

/**
 * GraphQL Currency enum - generated from CURRENCIES array
 * Used for GraphQL schema and @IsEnum validation
 */
export const CurrencyCode = Object.fromEntries(CURRENCIES.map(c => [c.code, c.code])) as Record<
  string,
  string
>;

export type CurrencyCode = (typeof CurrencyCode)[keyof typeof CurrencyCode];

const _currencyMap = new Map<string, ICurrency>(CURRENCIES.map(c => [c.code, c]));

/**
 * Returns the ICurrency for the given ISO 4217 code, or undefined if not found.
 */
export function getCurrency(code: string | null | undefined): ICurrency | undefined {
  if (isNil(code)) return undefined;
  return _currencyMap.get(code.toUpperCase());
}

/**
 * Returns the number of decimal places for a currency code.
 * Defaults to 2 if the currency is unknown.
 */
export function getCurrencyDecimalPlaces(code: CurrencyCode): number {
  return _currencyMap.get(code.toUpperCase())?.decimalPlaces ?? 2;
}
